import { MosError } from 'src/utils/errors/MosError';
import { getRuntimeConfig } from 'src/utils/runtimeConfig';
import { getCurrentTenantId } from 'src/stores/tenant';
import { RoutingStrategy } from 'src/utils/routing/type';
import { buildApiUrl } from 'src/utils/routing/apiUrlBuilder';
import { getRoutingStrategy } from 'src/utils/routing/routingConfig';
import { getQueryClient } from 'src/utils/queryClientRegistry';
import { handleSessionTermination } from 'src/utils/errors/sessionTerminationHandler';
import { captureSessionMessage } from 'src/utils/sentry/sessionSentry';

export interface HttpResponse<T> {
  success: boolean;
  status: number;
  data: T | null;
  statusText: string;
}

export interface PaginateConfig extends Omit<RequestInit, 'body'> {
  body?: object | FormData;
  paginate?: boolean;
  accumulateKey?: string;
  nextCursorKey?: string;
  limit?: number;
  routingStrategy?: RoutingStrategy;
  tenantId?: string | null;
}

interface PaginationContext {
  baseUrl: string;
  existingQueryString: string;
  existingParams: URLSearchParams;
  effectiveLimit: string | null;
  effectiveRoutingStrategy: RoutingStrategy;
  tenantId: string | null;
  headers: HeadersInit;
  config: PaginateConfig;
}

interface PaginationResult<T> {
  items: T[];
  thumbnails: Record<string, string[]>;
}

/** Throws MosError for network errors, re-throws other errors */
function handleNetworkError(error: unknown): never {
  if (error instanceof TypeError) {
    throw new MosError({
      message:
        'Unable to connect to the server. Please check your connection and try again.',
      statusCode: 0,
    });
  }
  throw error;
}

/** Prepares request body and content-type header based on body type */
function prepareRequestBody(config: PaginateConfig): {
  body: BodyInit | null;
  contentTypeHeader: { 'Content-Type'?: string };
} {
  const isFormData = config.body instanceof FormData;
  if (isFormData) {
    return { body: config.body as FormData, contentTypeHeader: {} };
  }
  return {
    body: JSON.stringify(config.body),
    contentTypeHeader: { 'Content-Type': 'application/json; charset=UTF-8' },
  };
}

const TENANT_ID_PLACEHOLDERS = new Set(['replace-this']);

function normalizeTenantId(tenantId: string | null | undefined): string | null {
  if (typeof tenantId !== 'string') return null;

  const trimmedTenantId = tenantId.trim();
  if (!trimmedTenantId || TENANT_ID_PLACEHOLDERS.has(trimmedTenantId)) {
    return null;
  }

  return trimmedTenantId;
}

/** Resolves tenant ID from config, store, or environment */
function resolveTenantId(config: PaginateConfig): string | null {
  const dynamicTenantId = getCurrentTenantId();
  const runtimeConfig = getRuntimeConfig();
  const envTenantId =
    typeof runtimeConfig.VITE_TENANT_ID === 'string'
      ? runtimeConfig.VITE_TENANT_ID
      : '';

  return (
    normalizeTenantId(config.tenantId) ??
    normalizeTenantId(dynamicTenantId) ??
    normalizeTenantId(envTenantId)
  );
}

/** Determines effective routing strategy based on endpoint type */
function resolveRoutingStrategy(
  url: string,
  config: PaginateConfig,
): RoutingStrategy {
  const routingStrategy = config.routingStrategy ?? getRoutingStrategy();
  const isSharedEndpoint = typeof url === 'string' && url.startsWith('/shared');
  return isSharedEndpoint ? 'header' : routingStrategy;
}

/** Builds headers including tenant-id based on routing strategy */
function buildHeaders(
  config: PaginateConfig,
  contentTypeHeader: { 'Content-Type'?: string },
  effectiveRoutingStrategy: RoutingStrategy,
  tenantId: string | null,
): HeadersInit {
  const customHeaders = new Headers(config.headers ?? {});

  if (effectiveRoutingStrategy === 'header' && tenantId) {
    customHeaders.set('tenant-id', tenantId);
  } else if (effectiveRoutingStrategy === 'path') {
    customHeaders.delete('tenant-id');
  }

  const headerObj: Record<string, string> = {};
  customHeaders.forEach((value, key) => {
    headerObj[key] = value;
  });

  const runtimeConfig = getRuntimeConfig();
  if (
    runtimeConfig.VITE_SUPER_ADMIN_API_KEY &&
    runtimeConfig.VITE_SUPER_ADMIN_API_KEY !== 'FOR_LOCAL_DEVELOPMENT_ONLY'
  ) {
    headerObj['x-api-key'] = runtimeConfig.VITE_SUPER_ADMIN_API_KEY as string;
  }

  return {
    Accept: 'application/json',
    ...contentTypeHeader,
    ...(headerObj as HeadersInit),
  };
}

/** Builds query params for paginated request */
function buildPaginationQueryParams(
  existingParams: URLSearchParams,
  cursor: string,
  effectiveLimit: string | null,
): string[] {
  const queryParams: string[] = [];

  existingParams.forEach((value, key) => {
    if (key !== 'cursor' && key !== 'limit') {
      queryParams.push(`${key}=${value}`);
    }
  });

  queryParams.push(`cursor=${cursor}`);

  if (effectiveLimit) {
    queryParams.push(`limit=${effectiveLimit}`);
  }

  return queryParams;
}

/** Fetches a single page during pagination */
async function fetchPaginationPage(
  cursor: string,
  ctx: PaginationContext,
): Promise<Response> {
  const queryParams = buildPaginationQueryParams(
    ctx.existingParams,
    cursor,
    ctx.effectiveLimit,
  );

  let pagedUrl = queryParams.length
    ? `${ctx.baseUrl}?${queryParams.join('&')}`
    : ctx.baseUrl;
  pagedUrl = buildApiUrl(pagedUrl, {
    strategy: ctx.effectiveRoutingStrategy,
    tenantId: ctx.tenantId,
  });

  const req = new Request(pagedUrl, {
    ...ctx.config,
    headers: ctx.headers,
    body: null,
  });

  try {
    return await fetch(req);
  } catch (error) {
    handleNetworkError(error);
  }
}

/** Processes page data and accumulates items and thumbnails */
function processPageData<T>(
  data: Record<string, unknown>,
  accumulateKey: string,
  result: PaginationResult<T>,
): void {
  if (data[accumulateKey]) {
    result.items = result.items.concat(data[accumulateKey] as T[]);
  }
  if (data.pagesThumbnails) {
    result.thumbnails = {
      ...result.thumbnails,
      ...(data.pagesThumbnails as Record<string, string[]>),
    };
  }
}

/**
 * Handles paginated GET requests, reusing initial response as first page.
 *
 * IMPORTANT: This function reuses the initial fetch response as the first page
 * of data to avoid making duplicate requests to the same endpoint.
 *
 * Bug Fix (MVP-1969): Previously, pagination made a duplicate request:
 * 1. Initial fetch() at line ~330 would succeed (200 OK)
 * 2. Pagination loop would fetch the SAME URL again, potentially failing (403)
 *
 * The 403 occurred because MSAL token refresh between the two requests caused
 * the backend to see a different token hash, invalidating the session.
 *
 * Solution: Reuse the initial response instead of making a second request.
 * This eliminates the race condition and ensures only N requests are made
 * for N pages (not N+1).
 *
 * @see test/services/utils/http.test.ts for regression tests
 */
async function handlePaginatedRequest<T>(
  response: Response,
  url: string,
  config: PaginateConfig,
  effectiveRoutingStrategy: RoutingStrategy,
  tenantId: string | null,
  headers: HeadersInit,
): Promise<HttpResponse<T>> {
  const accumulateKey = config.accumulateKey ?? 'data';
  const nextCursorKey = config.nextCursorKey ?? 'nextCursor';

  if (!response.ok) {
    return {
      success: response.ok,
      status: response.status,
      statusText: response.statusText,
      data: null,
    };
  }

  // Parse URL for pagination context
  const urlParts = url.split('?');
  const baseUrl = urlParts[0];
  const existingQueryString = urlParts[1] ?? '';
  const existingParams = new URLSearchParams(existingQueryString);
  const existingLimit = existingParams.has('limit')
    ? existingParams.get('limit')
    : null;
  const limit = config.limit ?? 100;
  const effectiveLimit = existingLimit ?? (limit ? String(limit) : null);

  const ctx: PaginationContext = {
    baseUrl,
    existingQueryString,
    existingParams,
    effectiveLimit,
    effectiveRoutingStrategy,
    tenantId,
    headers,
    config,
  };

  const result: PaginationResult<T> = { items: [], thumbnails: {} };

  // Process first page from initial response (avoids duplicate request)
  const firstPageData = await response.json();
  processPageData(firstPageData, accumulateKey, result);

  let cursor: string | null =
    (firstPageData[nextCursorKey] as string | null) ?? null;

  // Fetch additional pages
  while (cursor) {
    const resp = await fetchPaginationPage(cursor, ctx);
    if (!resp.ok) break;

    const data = await resp.json();
    if (!data[accumulateKey]) break;

    processPageData(data, accumulateKey, result);
    cursor = (data[nextCursorKey] as string | null) ?? null;
  }

  const output: Record<string, unknown> = {
    [accumulateKey]: result.items,
    status: true,
  };
  if (Object.keys(result.thumbnails).length > 0) {
    output.pagesThumbnails = result.thumbnails;
  }

  return {
    success: response.ok,
    status: response.status,
    statusText: response.statusText,
    data: output as T,
  };
}

/** Handles HTTP error responses by extracting error message from body */
async function handleErrorResponse(response: Response): Promise<never> {
  try {
    const body = await response.json();

    // Check for token invalidation error before creating MosError
    if (
      body.error?.code === 'FORBIDDEN' &&
      body.error?.message === 'Token has been invalidated'
    ) {
      captureSessionMessage(
        'Backend token invalidated -> terminating UI session',
        'warning',
        {
          event: 'session.termination.tokenInvalidated.http',
          extra: { errorCode: body.error?.code },
        },
      );

      // Try to handle session termination at HTTP layer
      const queryClient = getQueryClient();
      if (queryClient) {
        handleSessionTermination(queryClient).catch((err) => {
          console.error('Error during session termination:', err);
        });
      }
    }

    if (body.error?.message) {
      throw new MosError({
        message: body.error.message,
        statusCode: response.status,
        errorCode: body.error.errorCode ?? body.error.code,
        rawBody: body,
      });
    }

    if (body.message) {
      throw new MosError({
        ...body,
        statusCode: response.status,
      });
    }

    throw new MosError({
      message: body.error ?? `HTTP ${response.status}: ${response.statusText}`,
      statusCode: response.status,
      errorCode: body.error?.code,
      rawBody: body,
    });
  } catch (parseError) {
    if (parseError instanceof MosError) {
      throw parseError;
    }
    console.warn('Failed to parse error response:', parseError);
    throw new MosError({
      message: `HTTP ${response.status}: ${response.statusText}`,
      statusCode: response.status,
    });
  }
}

/** Main HTTP client function */
const http = async <T>(
  url: string,
  config: PaginateConfig = {},
): Promise<HttpResponse<T>> => {
  const { body, contentTypeHeader } = prepareRequestBody(config);
  const tenantId = resolveTenantId(config);
  const effectiveRoutingStrategy = resolveRoutingStrategy(url, config);
  const headers = buildHeaders(
    config,
    contentTypeHeader,
    effectiveRoutingStrategy,
    tenantId,
  );

  const apiUrl = buildApiUrl(url, {
    strategy: effectiveRoutingStrategy,
    tenantId: tenantId,
  });

  const request = new Request(apiUrl, { ...config, headers, body });

  let response: Response;
  try {
    response = await fetch(request);
  } catch (error) {
    handleNetworkError(error);
  }

  // Handle paginated GET requests
  const isGetRequest = (config.method ?? 'GET').toUpperCase() === 'GET';
  if (config?.paginate && isGetRequest) {
    return handlePaginatedRequest<T>(
      response,
      url,
      config,
      effectiveRoutingStrategy,
      tenantId,
      headers,
    );
  }

  if (!response.ok) {
    await handleErrorResponse(response);
  }

  return {
    success: response.ok,
    status: response.status,
    statusText: response.statusText,
    data:
      config.method === 'DELETE'
        ? null
        : ((await response.json()) as unknown as T),
  };
};

export default http;
